import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Globe2 } from "lucide-react";
import {
  getUniverseLeaderboard,
  type UniverseResponse,
} from "@/lib/universe.functions";
import { UniverseLeaderboard } from "@/components/UniverseLeaderboard";

export const Route = createFileRoute("/_app/universe")({
  component: UniversePage,
  head: () => ({
    meta: [
      { title: "The Universe — Bracket Goblin" },
      {
        name: "description",
        content:
          "Every bracket across every league in one ranked list. See where your picks stand against the whole site.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function UniversePage() {
  const navigate = useNavigate();
  const fetchUniverse = useServerFn(getUniverseLeaderboard);
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data, isLoading, error } = useQuery<UniverseResponse>({
    queryKey: ["universe-leaderboard"],
    queryFn: () => fetchUniverse(),
    staleTime: 60_000,
  });

  // No eligible leagues (nothing locked yet) — bounce home. The inline card
  // and footer link are hidden pre-lock too, but a direct URL visit is
  // possible so we still handle it.
  useEffect(() => {
    if (data && !data.available) {
      navigate({ to: "/", replace: true });
    }
  }, [data, navigate]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: ["universe-leaderboard"] });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-6 sm:py-10">
      <div className="mb-6 sm:mb-8">
        <Link
          to="/"
          className="mb-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to your leagues
        </Link>
        <div className="flex items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">
            <Globe2 className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-bold leading-tight sm:text-4xl">
              The Universe
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Every bracket across every league, ranked together. Bracket names
              only — no picks, no personal info.
            </p>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center text-sm text-muted-foreground">
          Loading universe…
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          Couldn't load the universe leaderboard.{" "}
          <button
            className="underline hover:no-underline"
            onClick={handleRefresh}
          >
            Try again
          </button>
        </div>
      )}

      {data && data.available && (
        <UniverseLeaderboard
          rows={data.rows}
          viewerEntryIds={data.viewerEntryIds}
          leagueCount={data.leagueCount}
          computedAt={data.computedAt}
          onRefresh={handleRefresh}
          isRefreshing={isRefreshing}
        />
      )}
    </main>
  );
}
